import { safeDefine } from "@breuni-ui/safe-define";
import RecoLoader from "./RecoLoader";
import {
  fetchRecommendations,
  RecommendationResponse,
} from "./fetchRecommendations";
import RecoConsent from "./RecoConsent";
import RichRelevanceTracking from "./tracking/richRelevance/RichRelevanceTracking";

export type VariantenWechselDetails = {
  syan: string;
  farbId?: string;
  artikelId?: string;
};

const variantenWechselEvent = "bewe:ents:variantenwechsel" as const;

const notNull = (x: string | null): x is string => x !== null;

declare global {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface DocumentEventMap {
    [variantenWechselEvent]: CustomEvent<VariantenWechselDetails>;
  }
}

const recoLoaderTagName = "entd-multi-instance-reco-loader" as const;

export default class MultiInstanceRecoLoader extends RecoLoader {
  private recoLoaders: MultiInstanceRecoLoader[];
  private readonly boundHandleEvent: (
    e: CustomEvent<VariantenWechselDetails>,
  ) => void;
  private readonly boundSetupRecommendationsLoading: () => void;

  constructor() {
    super();
    this.recoLoaders = [];
    this.boundHandleEvent = this.handleFarbwechselEvent.bind(this);
    this.boundSetupRecommendationsLoading =
      this.setupRecommendationsLoading.bind(this);
  }

  connectedCallback(): void {
    super.connectedCallback();
    if (document.readyState === "loading") {
      document.addEventListener(
        "DOMContentLoaded",
        this.boundSetupRecommendationsLoading,
      );
    } else {
      this.setupRecommendationsLoading();
    }
  }

  disconnectedCallback(): void {
    document.removeEventListener(variantenWechselEvent, this.boundHandleEvent);
    document.removeEventListener(
      "DOMContentLoaded",
      this.boundSetupRecommendationsLoading,
    );
  }

  private setupRecommendationsLoading(): void {
    this.recoLoaders = Array.from(
      document.querySelectorAll<MultiInstanceRecoLoader>(recoLoaderTagName),
    );
    const placements = this.getPlacements();
    if (this.determineLeader(placements)) {
      RecoConsent.setupQueue();
      this.richRelevanceConsent = RecoConsent.getRichRelevanceConsent();
      this.personalisierungConsent = RecoConsent.getPersonalisierungConsent();

      if (this.recoLoaderProps && this.recoLoaderProps.countryUrlPrefix) {
        const origin = window.location.origin;
        const countryPrefix = this.recoLoaderProps.countryUrlPrefix;
        RichRelevanceTracking.sendTrackingInfo(
          this.richRelevanceConsent,
          countryPrefix,
          origin,
        );
        fetchRecommendations(window.fetch)(
          placements,
          countryPrefix,
          this.recoLoaderProps.queryParameters,
          origin,
          this.richRelevanceConsent,
          this.personalisierungConsent,
          null,
        )
          .then(this.renderRecommendations.bind(this))
          .catch(this.removePlaceHolders.bind(this));
      }

      document.addEventListener(variantenWechselEvent, this.boundHandleEvent, {
        passive: true,
      });
    }
  }

  private getPlacements(): string[] {
    return this.recoLoaders
      .map((recoLoader) => recoLoader.getAttribute("placement"))
      .filter(notNull)
      .sort();
  }

  private determineLeader(placements: string[]): boolean {
    const placement = this.getAttribute("placement");
    if (placement) {
      const index = placements.indexOf(placement);
      return index === 0;
    } else {
      return false;
    }
  }

  private handleFarbwechselEvent(
    event: CustomEvent<VariantenWechselDetails>,
  ): void {
    const origin = window.location.origin;
    if (this.recoLoaderProps && this.recoLoaderProps.countryUrlPrefix) {
      const countryPrefix = this.recoLoaderProps.countryUrlPrefix;
      fetchRecommendations(window.fetch)(
        this.getPlacements(),
        countryPrefix,
        this.recoLoaderProps.queryParameters,
        origin,
        this.richRelevanceConsent,
        this.personalisierungConsent,
        event.detail,
      )
        .then(this.renderRecommendations.bind(this))
        .catch(this.removePlaceHolders.bind(this));
    }
  }

  private renderRecommendations(
    recommendationResponse: RecommendationResponse,
  ): Promise<void[]> {
    const results = this.recoLoaders.map((loader) => {
      const placement = loader.getAttribute("placement");
      if (
        placement &&
        Object.prototype.hasOwnProperty.call(recommendationResponse, placement)
      ) {
        const recommendations = recommendationResponse[placement];
        return loader.renderRecommendation(recommendations);
      }
    });
    return Promise.all(results).then((res) => {
      if (
        this.recoLoaders.some(
          (loader) => loader.recoLoaderProps?.aktionsFlagsEnabled === true,
        )
      ) {
        document.dispatchEvent(this.campAktionsflagsInitialized);
      }
      return res;
    });
  }

  private removePlaceHolders(): Promise<void[]> {
    return Promise.all(
      this.recoLoaders.map((loader) => {
        return loader.renderRecommendation("");
      }),
    );
  }
}

safeDefine(recoLoaderTagName, MultiInstanceRecoLoader);
